import { gettext } from '@eventbrite/i18n';
import { $FixMe } from '@eventbrite/ts-utils';
import invert from 'lodash/invert';
import queryString from 'query-string';
import React from 'react';
import { withRouter } from 'react-router-dom';
import {
    DATES_MAPPING_SEARCH,
    DATES_TO_REFINEMENT,
    EVENTBRITE_CATEGORY_MAPPING_SEARCH,
    EVENTBRITE_CATEGORY_TO_REFINEMENT,
    EVENTBRITE_FORMAT_MAPPING_SEARCH,
    EVENTBRITE_FORMAT_TO_REFINEMENT,
    PRICE_MAPPING,
} from '../../../../constants';
import {
    CURRENCY_FILTER,
    DATE_FILTER,
    FORMATTED_CURRENCIES,
    FORMATTED_LANGUAGES,
    FORMAT_FILTER,
    LANGUAGE_FILTER,
    PRICE_FILTER,
    TAGS_FILTER,
} from '../../../../constants/filters';
import { FiltersShape } from '../../../../types/index';
import DateFilterSection from './DateFilterSection';
import FilterCheckboxGroup from './FilterCheckboxGroup';
import FilterSection from './FilterSection';
import LazyInlineDatePicker from './LazyInlineDatePicker';

const DateFilters = (props: FiltersShape) => {
    const urlParams = queryString.parse(location.search.replace('?', ''));

    return (
        <DateFilterSection
            filters={{
                all: gettext('Any day'),
                ...DATES_MAPPING_SEARCH,
            }}
            onOptionChange={(value: any, redirect: any) =>
                props.onFilterChange?.(DATE_FILTER, value, redirect)
            }
            onCustomDate={(startDate: any, endDate: any) =>
                props.onCustomDateChange?.(startDate, endDate)
            }
            onDatePickerSelected={
                props.isMobile
                    ? () => props.setDatePickerDrawerVisible?.(true)
                    : () => {
                          return;
                      }
            }
            DatePickerComponent={
                <LazyInlineDatePicker
                    onDatePickerChange={props.onCustomDateChange}
                    urlParams={urlParams}
                />
            }
            urlParameterMapping={invert(DATES_TO_REFINEMENT)}
            isMobile={props.isMobile}
            isDrawerVisible={props.isDatePickerDrawerVisible}
        />
    );
};

const CategoryFilters = (props: FiltersShape) => (
    <FilterSection
        isMobile={props.isMobile}
        filters={{
            all: gettext('Any category'),
            ...EVENTBRITE_CATEGORY_MAPPING_SEARCH,
        }}
        onOptionChange={(value: any, redirect: any) =>
            props.onFilterChange?.(TAGS_FILTER, value, redirect)
        }
        urlParameterMapping={invert(EVENTBRITE_CATEGORY_TO_REFINEMENT)}
    />
);

const FormatFilters = (props: FiltersShape) => (
    <FilterSection
        isMobile={props.isMobile}
        filters={{
            all: gettext('Any format'),
            ...EVENTBRITE_FORMAT_MAPPING_SEARCH,
        }}
        onOptionChange={(value: any, redirect: any) =>
            props.onFilterChange?.(FORMAT_FILTER, value, redirect)
        }
        urlParameterMapping={invert(EVENTBRITE_FORMAT_TO_REFINEMENT)}
    />
);

const LanguageFilters = (props: FiltersShape) => (
    <FilterSection
        isMobile={props.isMobile}
        filters={{
            all: gettext('Any language'),
            ...FORMATTED_LANGUAGES,
        }}
        onOptionChange={(value: any, redirect: any) =>
            props.onFilterChange?.(LANGUAGE_FILTER, value, redirect)
        }
        urlParameterMapping={{
            lang: LANGUAGE_FILTER,
            isInQuery: true,
        }}
        //Custom render necessary for multiselect style filters
        renderFilters={(
            filters: any,
            selectedFilter: any,
            onClick: (arg0: any, arg1: boolean, arg2: boolean) => any,
        ) => {
            return (
                <div className="filter-section__checkbox-group">
                    <FilterCheckboxGroup
                        values={filters}
                        selectedFilters={selectedFilter}
                        onClick={(values: any) => onClick(values, true, true)}
                        name={`language-options--${
                            props.isMobile ? 'mobile' : 'desktop'
                        }`}
                        isMobile={props.isMobile}
                    />
                </div>
            );
        }}
    />
);

const CurrencyFilters = (props: FiltersShape) => (
    <FilterSection
        isMobile={props.isMobile}
        filters={{
            all: gettext('Any currency'),
            ...FORMATTED_CURRENCIES,
        }}
        onOptionChange={(value: any, redirect: any) =>
            props.onFilterChange?.(CURRENCY_FILTER, value, redirect)
        }
        urlParameterMapping={{
            cur: CURRENCY_FILTER,
            isInQuery: true,
        }}
    />
);

const PriceFilters = (props: FiltersShape) => {
    return (
        <div className="eds-l-mar-bot-20">
            <FilterSection
                isMobile={props.isMobile}
                filters={{
                    all: gettext('Any price'),
                    ...PRICE_MAPPING,
                }}
                onOptionChange={(value: any, redirect: any) =>
                    props.onFilterChange?.(PRICE_FILTER, value, redirect)
                }
                useAppState={true}
                activeFilterOption={
                    props.selectedFilters?.[PRICE_FILTER]
                        ? props.selectedFilters?.[PRICE_FILTER]
                        : 'all'
                }
            />
        </div>
    );
};

const shouldShowFilterSection = (
    filterTypeToShow?: $FixMe,
    isDatePickerDrawerVisible?: boolean,
    filterTypeConstant?: string,
) => {
    if (isDatePickerDrawerVisible) {
        return filterTypeConstant === DATE_FILTER;
    }

    return (
        !filterTypeToShow ||
        (filterTypeToShow && filterTypeToShow === filterTypeConstant)
    );
};

const Filters = (props: FiltersShape) => {
    const shouldShow = shouldShowFilterSection.bind(
        null,
        props.filterTypeToShow,
        props.isDatePickerDrawerVisible,
    );

    return (
        <>
            {shouldShow(DATE_FILTER) && <DateFilters {...props} />}
            {shouldShow(TAGS_FILTER) && <CategoryFilters {...props} />}
            {shouldShow(FORMAT_FILTER) && <FormatFilters {...props} />}
            {shouldShow(LANGUAGE_FILTER) && <LanguageFilters {...props} />}
            {shouldShow(CURRENCY_FILTER) && <CurrencyFilters {...props} />}
            {shouldShow(PRICE_FILTER) && <PriceFilters {...props} />}
        </>
    );
};

export default withRouter(Filters);
